// components/AddHighlightBookModal.tsx
// 「メモする本を追加」モーダル。「本を検索」「本棚から選択」の2方式で本を選び、
// rl_highlight_books だけを登録する（実際のハイライト文章はこの後の画面で書く）。
// モバイル版 add_highlight_book_by_search_screen.dart / add_highlight_book_by_hondana.dart の
// Web版だが、既に追加済みの本は+ボタンを非活性にする点が異なる（モバイルは重複追加を防いでいない）

import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { useAuth } from '../context/AuthContext';
import { useAppTokens } from '../context/ThemeModeContext';
import { NEW_API, buildPrefixSearchUrl } from '../config/api';
import { fetchShelvesRaw, ShelfDetail } from '../utils/shelfHandler';
import { registerHighlightBook } from '../utils/highlightsApi';
import { generateCoverUrlSmart } from '../utils/generateCoverUrl';
import { titleWithVolume } from '../utils/bibAdapter';
import { PrefixSearchBook } from '../types/rlTypes';

interface ShelfBookRow {
  global_book_id: string;
  rl_book_id: number;
  title: string;
  author: string;
  isbn13: string | null;
  volume?: string | number | null;
}

type Step = 'choose' | 'search' | 'shelf';

interface AddHighlightBookModalProps {
  open: boolean;
  onClose: () => void;
  /** 既に「抜粋・メモする本」として登録済みの rl_book_id */
  registeredBookIds: Set<number>;
  /** 本が新たに登録された時に呼ばれる（呼び出し元の一覧更新用） */
  onBookAdded: (rlBookId: number) => void;
}

const UNSHELVED_KEY = '__none__';

const AddHighlightBookModal: React.FC<AddHighlightBookModalProps> = ({
  open,
  onClose,
  registeredBookIds,
  onBookAdded,
}) => {
  const TOKENS = useAppTokens();
  const { idToken } = useAuth();

  const [step, setStep] = useState<Step>('choose');

  // このモーダルを開いている間に追加した本（親からの再取得を待たずに即座にボタンを更新するため）
  const [locallyAddedIds, setLocallyAddedIds] = useState<Set<number>>(new Set());
  const [addingId, setAddingId] = useState<number | null>(null);

  // 「本を検索」
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<PrefixSearchBook[]>([]);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);

  // 「本棚から選択」
  const [shelves, setShelves] = useState<ShelfDetail[]>([]);
  const [shelvesLoading, setShelvesLoading] = useState(false);
  const [shelfBooksCache, setShelfBooksCache] = useState<Record<string, ShelfBookRow[]>>({});
  const [loadingShelfKeys, setLoadingShelfKeys] = useState<Set<string>>(new Set());
  const [shelfSearchText, setShelfSearchText] = useState('');

  useEffect(() => {
    if (!open) return;
    // モーダルを開くたびに最初の選択画面に戻す
    setStep('choose');
    setLocallyAddedIds(new Set());
    setSearchQuery('');
    setSearchResults([]);
    setSearched(false);
    setShelves([]);
    setShelfBooksCache({});
    setLoadingShelfKeys(new Set());
    setShelfSearchText('');
  }, [open]);

  const isAdded = useCallback(
    (rlBookId: number) => registeredBookIds.has(rlBookId) || locallyAddedIds.has(rlBookId),
    [registeredBookIds, locallyAddedIds],
  );

  const handleAdd = async (rlBookId: number) => {
    if (!idToken || isAdded(rlBookId) || addingId != null) return;
    setAddingId(rlBookId);
    try {
      await registerHighlightBook(rlBookId, idToken);
      setLocallyAddedIds((prev) => new Set(prev).add(rlBookId));
      onBookAdded(rlBookId);
    } catch (e) {
      console.error('Error registering highlight book:', e);
    } finally {
      setAddingId(null);
    }
  };

  const runSearch = async () => {
    const query = searchQuery.trim();
    if (!query) return;
    setSearching(true);
    setSearched(true);
    try {
      const res = await axios.get(buildPrefixSearchUrl(query, 50, 0));
      setSearchResults(res.data?.books ?? []);
    } catch (e) {
      console.error('Error searching books:', e);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const openShelfStep = async () => {
    setStep('shelf');
    if (shelves.length > 0 || !idToken) return;
    setShelvesLoading(true);
    try {
      const items = await fetchShelvesRaw(idToken);
      items.sort((a, b) => (a.global_shelf_order ?? 0) - (b.global_shelf_order ?? 0));
      setShelves(items);
    } catch (e) {
      console.error('Error fetching shelves:', e);
    } finally {
      setShelvesLoading(false);
    }
  };

  const loadShelfGroup = useCallback(
    async (key: string, opts?: { shelfId?: string; noShelfOnly?: boolean }) => {
      if (!idToken || shelfBooksCache[key] || loadingShelfKeys.has(key)) return;
      setLoadingShelfKeys((prev) => new Set(prev).add(key));
      try {
        const headers = { Authorization: `Bearer ${idToken}` };
        const rows: ShelfBookRow[] = [];
        let cursor: string | null = null;
        do {
          const params: Record<string, string | number> = { limit: 100 };
          if (cursor) params.cursor = cursor;
          if (opts?.shelfId) params.shelf_id = opts.shelfId;
          if (opts?.noShelfOnly) params.no_shelf_only = 'true';
          const res = await axios.get(NEW_API.BOOKS, { headers, params });
          const data = res.data?.data ?? {};
          (data.items ?? []).forEach((item: any) => {
            rows.push({
              global_book_id: item.global_book_id,
              rl_book_id: item.rl_book_id,
              title: item.title ?? '(タイトル未登録)',
              author: item.author ?? '',
              isbn13: item.isbn13 ?? null,
              volume: item.volume ?? null,
            });
          });
          cursor = data.next_cursor ?? null;
        } while (cursor);
        setShelfBooksCache((prev) => ({ ...prev, [key]: rows }));
      } catch (e) {
        console.error('Error fetching shelf books:', e);
      } finally {
        setLoadingShelfKeys((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
      }
    },
    [idToken, shelfBooksCache, loadingShelfKeys],
  );

  const addButtonSx = (added: boolean) => ({
    width: 30,
    height: 30,
    minWidth: 0,
    borderRadius: '999px',
    p: 0,
    flexShrink: 0,
    background: added ? '#e5e7eb' : '#22c55e',
    color: added ? '#9ca3af' : '#ffffff',
    '&:hover': { background: added ? '#e5e7eb' : '#16a34a' },
    '&.Mui-disabled': { background: '#e5e7eb', color: '#9ca3af' },
  });

  const renderAddButton = (rlBookId: number) => {
    const added = isAdded(rlBookId);
    const isBusy = addingId === rlBookId;
    return (
      <IconButton
        size="small"
        disabled={added || isBusy}
        onClick={() => handleAdd(rlBookId)}
        title={added ? 'メモに追加済みです' : 'この本に抜粋・メモを追加'}
        sx={addButtonSx(added)}
      >
        {isBusy ? (
          <CircularProgress size={14} sx={{ color: '#ffffff' }} />
        ) : added ? (
          <CheckIcon sx={{ fontSize: 16 }} />
        ) : (
          <AddIcon sx={{ fontSize: 18 }} />
        )}
      </IconButton>
    );
  };

  const filterShelfRows = (rows: ShelfBookRow[]) => {
    const keyword = shelfSearchText.trim().toLowerCase();
    if (!keyword) return rows;
    return rows.filter(
      (r) =>
        r.title.toLowerCase().includes(keyword) ||
        r.author.toLowerCase().includes(keyword) ||
        (r.isbn13 ?? '').toLowerCase().includes(keyword),
    );
  };

  const glassPanelSx = {
    background: TOKENS.glass,
    border: `1px solid ${TOKENS.border}`,
    borderRadius: '14px',
  } as const;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { background: TOKENS.bg, color: TOKENS.text, backgroundImage: 'none' } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        <span>メモする本を追加</span>
        <IconButton onClick={onClose} size="small" sx={{ color: TOKENS.textSub }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pb: 3, minHeight: 320 }}>
        {step === 'choose' && (
          <Box>
            <Typography sx={{ fontSize: 13.5, color: TOKENS.textSub, mb: 2.5 }}>
              メモする本を追加する方法を選択してください。
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Button
                onClick={() => setStep('search')}
                startIcon={<SearchIcon />}
                sx={{
                  ...glassPanelSx,
                  justifyContent: 'flex-start',
                  textTransform: 'none',
                  color: TOKENS.text,
                  fontSize: 14,
                  fontWeight: 600,
                  py: 1.5,
                  px: 2,
                  '&:hover': { borderColor: TOKENS.accent, background: TOKENS.cardBgHover },
                }}
              >
                本を検索
              </Button>
              <Button
                onClick={openShelfStep}
                startIcon={
                  <Box
                    component="span"
                    sx={{ display: 'inline-flex', width: 24, justifyContent: 'center' }}
                  >
                    📚
                  </Box>
                }
                sx={{
                  ...glassPanelSx,
                  justifyContent: 'flex-start',
                  textTransform: 'none',
                  color: TOKENS.text,
                  fontSize: 14,
                  fontWeight: 600,
                  py: 1.5,
                  px: 2,
                  '&:hover': { borderColor: TOKENS.accent, background: TOKENS.cardBgHover },
                }}
              >
                本棚から選択
              </Button>
            </Box>
          </Box>
        )}

        {step === 'search' && (
          <Box>
            <Button
              onClick={() => setStep('choose')}
              sx={{ mb: 1.5, ml: -1, textTransform: 'none', color: TOKENS.textSub }}
            >
              ← 戻る
            </Button>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                fullWidth
                size="small"
                autoFocus
                placeholder="タイトル・著者名で検索"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') runSearch();
                }}
                sx={{
                  '& .MuiOutlinedInput-root': { color: TOKENS.text, background: TOKENS.buttonBg, borderRadius: '10px' },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: TOKENS.borderSoft },
                }}
              />
              <Button
                onClick={runSearch}
                disabled={!searchQuery.trim() || searching}
                sx={{
                  textTransform: 'none',
                  borderRadius: '10px',
                  px: 2,
                  border: `1px solid ${TOKENS.borderSoft}`,
                  color: TOKENS.bg,
                  background: TOKENS.text,
                  '&:hover': { background: TOKENS.textSub },
                  '&.Mui-disabled': {
                    color: TOKENS.textSub,
                    background: 'transparent',
                    border: `1px solid ${TOKENS.borderSoft}`,
                  },
                }}
              >
                検索
              </Button>
            </Box>

            {searching ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={22} sx={{ color: TOKENS.text }} />
              </Box>
            ) : !searched ? (
              <Typography sx={{ fontSize: 12.5, color: TOKENS.textSub, textAlign: 'center', py: 4 }}>
                書名や著者名で検索してください。
              </Typography>
            ) : searchResults.length === 0 ? (
              <Typography sx={{ fontSize: 12.5, color: TOKENS.textSub, textAlign: 'center', py: 4 }}>
                該当する本が見つかりませんでした。
              </Typography>
            ) : (
              <TableContainer sx={{ background: '#ffffff', borderRadius: '10px', maxHeight: 380 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ background: '#ffffff' }}>書影</TableCell>
                      <TableCell sx={{ background: '#ffffff' }}>タイトル</TableCell>
                      <TableCell sx={{ background: '#ffffff' }}>巻数</TableCell>
                      <TableCell sx={{ background: '#ffffff' }}>責任表示</TableCell>
                      <TableCell sx={{ background: '#ffffff' }}>出版社</TableCell>
                      <TableCell sx={{ background: '#ffffff' }}>出版年</TableCell>
                      <TableCell sx={{ background: '#ffffff' }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {searchResults.map((book) => (
                      <TableRow key={book.rl_book_id}>
                        <TableCell sx={{ p: 0.75 }}>
                          <Box
                            component="img"
                            src={generateCoverUrlSmart(book.rl_book_id, book.title, book.isbn13)}
                            alt={book.title}
                            sx={{ width: 32, height: 44, objectFit: 'contain', background: '#f3f4f6', borderRadius: '3px' }}
                          />
                        </TableCell>
                        <TableCell sx={{ color: '#1a1a2e', fontSize: 12.5, maxWidth: 200 }}>{book.title}</TableCell>
                        <TableCell sx={{ color: '#1a1a2e', fontSize: 12.5 }}>{book.volume ?? ''}</TableCell>
                        <TableCell sx={{ color: '#4b5563', fontSize: 12, maxWidth: 160 }}>{book.responsibility ?? ''}</TableCell>
                        <TableCell sx={{ color: '#4b5563', fontSize: 12 }}>{book.publisher ?? ''}</TableCell>
                        <TableCell sx={{ color: '#4b5563', fontSize: 12 }}>{book.publishing_date ?? ''}</TableCell>
                        <TableCell sx={{ p: 0.75 }}>{renderAddButton(book.rl_book_id)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}

        {step === 'shelf' && (
          <Box>
            <Button
              onClick={() => setStep('choose')}
              sx={{ mb: 1.5, ml: -1, textTransform: 'none', color: TOKENS.textSub }}
            >
              ← 戻る
            </Button>

            {shelvesLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={22} sx={{ color: TOKENS.text }} />
              </Box>
            ) : (
              <>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="タイトル・著者名で絞り込み（開いた棚の中のみ）"
                  value={shelfSearchText}
                  onChange={(e) => setShelfSearchText(e.target.value)}
                  sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-root': { color: TOKENS.text, background: TOKENS.buttonBg, borderRadius: '10px' },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: TOKENS.borderSoft },
                  }}
                />
                {[
                  ...shelves.map((s) => ({
                    key: s.rl_global_shelf_id,
                    name: s.global_shelf_name,
                    countHint: s.book_count,
                    opts: { shelfId: s.rl_global_shelf_id },
                  })),
                  { key: UNSHELVED_KEY, name: '本棚未分類の本', countHint: undefined, opts: { noShelfOnly: true } },
                ].map((group) => {
                  const loadedBooks = shelfBooksCache[group.key];
                  const isLoadingGroup = loadingShelfKeys.has(group.key);
                  const visibleBooks = loadedBooks ? filterShelfRows(loadedBooks) : undefined;
                  const countLabel = loadedBooks
                    ? `${loadedBooks.length}冊`
                    : group.countHint != null
                    ? `${group.countHint}冊`
                    : '';

                  return (
                    <Accordion
                      key={group.key}
                      disableGutters
                      onChange={(_, expanded) => {
                        if (expanded) loadShelfGroup(group.key, group.opts);
                      }}
                      sx={{
                        mb: 1,
                        background: TOKENS.glass,
                        border: `1px solid ${TOKENS.border}`,
                        borderRadius: '12px !important',
                        '&:before': { display: 'none' },
                      }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: TOKENS.textSub }} />}>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: TOKENS.text }}>
                          {group.name}（{countLabel}）
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        {isLoadingGroup ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                            <CircularProgress size={18} sx={{ color: TOKENS.text }} />
                          </Box>
                        ) : visibleBooks && visibleBooks.length === 0 ? (
                          <Typography sx={{ fontSize: 12, color: TOKENS.textSub, textAlign: 'center', py: 1 }}>
                            {shelfSearchText ? '絞り込み条件に一致する本がありません' : 'この棚には本がありません'}
                          </Typography>
                        ) : (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {visibleBooks?.map((book) => (
                              <Box
                                key={book.global_book_id}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1.25,
                                  border: `1px solid ${TOKENS.border}`,
                                  background: TOKENS.cardBg,
                                  borderRadius: '10px',
                                  p: 1,
                                }}
                              >
                                <Box
                                  component="img"
                                  src={generateCoverUrlSmart(book.rl_book_id, book.title, book.isbn13)}
                                  alt={book.title}
                                  sx={{ width: 32, height: 44, objectFit: 'contain', borderRadius: '3px', background: TOKENS.cardBg, flexShrink: 0 }}
                                />
                                <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                                  <Typography sx={{ fontSize: 12.5, color: TOKENS.text }} noWrap>
                                    {titleWithVolume(book.title, book.volume)}
                                  </Typography>
                                  <Typography sx={{ fontSize: 11, color: TOKENS.textSub }} noWrap>
                                    {book.author}
                                  </Typography>
                                </Box>
                                {renderAddButton(book.rl_book_id)}
                              </Box>
                            ))}
                          </Box>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AddHighlightBookModal;
